import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { EmberParticles } from "@/components/EmberParticles";
import { HeroStats } from "@/components/HeroStats";
import { FeatureShowcase } from "@/components/FeatureShowcase";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { HomeFAQ } from "@/components/HomeFAQ";
import { 
  Flame, 
  ArrowRight, 
  Loader2, 
  ChevronDown,
  Anvil,
  Shield,
  Swords,
  HelpCircle
} from "lucide-react";

const Index = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/dashboard");
      }
      setLoading(false);
    });
  }, [navigate]);

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Hero Section - Full viewport */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-card/50" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsl(24_95%_53%_/_0.08)_0%,_transparent_60%)]" />
        
        {/* Ember particles */}
        <EmberParticles />
        
        {/* Decorative forge elements */}
        <div className="absolute top-1/4 left-0 w-px h-32 bg-gradient-to-b from-transparent via-primary/50 to-transparent" />
        <div className="absolute top-1/3 right-0 w-px h-48 bg-gradient-to-b from-transparent via-primary/30 to-transparent" />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-20">
          <div className="flex flex-col items-center text-center max-w-5xl mx-auto">
            {/* Logo with glow */}
            <div className="animate-fade-in mb-8">
              <div className="relative">
                <div className="absolute inset-0 blur-2xl bg-primary/20 scale-150" />
                <Logo size="lg" />
              </div>
            </div>

            {/* Main headline */}
            <h1 
              className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-display tracking-wider leading-none animate-fade-in break-words"
              style={{ animationDelay: "0.1s" }}
            >
              <span className="block text-foreground">FORGE YOUR</span>
              <span className="block text-gradient-ember mt-2">UNBREAKABLE SELF</span>
            </h1>

            {/* Brand definition */}
            <div 
              className="mt-6 flex flex-col items-center gap-1 animate-fade-in"
              style={{ animationDelay: "0.15s" }}
            >
              <p className="text-sm md:text-base italic text-muted-foreground/80">
                <span className="text-foreground/90 font-medium">Mod</span> — modify yourself deliberately.
              </p>
              <p className="text-sm md:text-base italic text-muted-foreground/80">
                <span className="text-foreground/90 font-medium">Mōd</span> — the inner spirit and fire of a man.
              </p>
            </div>

            {/* Subheadline */}
            <p 
              className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed animate-fade-in"
              style={{ animationDelay: "0.2s" }}
            >
              A personal reinvention engine for men who are done being stuck.
            </p>

            {/* Value props */}
            <div 
              className="flex flex-wrap justify-center gap-4 mt-8 animate-fade-in"
              style={{ animationDelay: "0.3s" }}
            >
              {[
                { icon: Anvil, text: "Build Habits" },
                { icon: Swords, text: "Track Goals" },
                { icon: Shield, text: "Break Vices" },
                { icon: Flame, text: "Earn GRIT" },
              ].map((item) => (
                <div 
                  key={item.text}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-border/50 bg-card/30 backdrop-blur-sm"
                >
                  <item.icon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div 
              className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-in"
              style={{ animationDelay: "0.4s" }}
            >
              <Button 
                variant="forge" 
                size="xl"
onClick={() => navigate("/auth?mode=signup")}
                className="group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Begin Your Transformation
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] animate-[shimmer_2s_infinite]" style={{ opacity: 0.3 }} />
              </Button>
              <Button 
                variant="steel" 
                size="xl"
                onClick={() => navigate("/auth")}
              >
                I Have an Account
              </Button>
            </div>

            {/* FAQ + Scroll indicator */}
            <div className="mt-16 flex flex-col items-center gap-4 animate-fade-in" style={{ animationDelay: "0.5s" }}>
              <a
                href="#faq"
                onClick={(e) => { e.preventDefault(); document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }); }}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-border/50 bg-card/30 backdrop-blur-sm text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">How does it work?</span>
              </a>
              <button 
                onClick={scrollToFeatures}
                className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <span className="text-xs uppercase tracking-widest">Discover More</span>
                <ChevronDown className="w-5 h-5 animate-bounce" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <HeroStats />

      {/* Features section */}
      <div id="features">
        <FeatureShowcase />
      </div>

      {/* Testimonials */}
      <TestimonialCarousel />

      {/* FAQ */}
      <HomeFAQ />

      {/* Final CTA */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_hsl(24_95%_53%_/_0.1)_0%,_transparent_60%)]" />
        
        <div className="container mx-auto max-w-3xl text-center relative z-10">
          <Flame className="w-16 h-16 mx-auto mb-8 text-primary animate-ember-pulse" />
          
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-display tracking-wider mb-6 break-words">
            <span className="block">YOUR TRANSFORMATION</span>
            <span className="block text-gradient-ember">STARTS NOW</span>
          </h2>
          
          <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
            Every master was once a disaster. Every champion started as a contender. 
            The only question is: are you ready to begin?
          </p>

          <Button 
            variant="forge" 
            size="xl"
            onClick={() => navigate("/auth")}
            className="shadow-ember"
          >
            Start Forging Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <p className="mt-6 text-sm text-muted-foreground">
            Free to start • No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border/30">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} MōdForge. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
